import * as THREE from "three";

export interface TrajectoryConfig {
  start: THREE.Vector3;
  end: THREE.Vector3;
  controlPoint1: THREE.Vector3;
  controlPoint2: THREE.Vector3;
  durationSeconds: number;
  easing?: ( progress: number ) => number;
}

export class CurveTrajectoryController {
  private curve: THREE.CubicBezierCurve3;
  private duration: number;
  private elapsed = 0;
  private easing: ( progress: number ) => number;

  constructor( config: TrajectoryConfig ) {
    this.curve = new THREE.CubicBezierCurve3(
      config.start.clone(),
      config.controlPoint1.clone(),
      config.controlPoint2.clone(),
      config.end.clone()
    );
    this.duration = config.durationSeconds;
    // Default to quadratic ease-in (constant acceleration from slow start to fast end)
    this.easing = config.easing ?? ( ( p: number ) => p * p );
  }

  public update( dt: number ): { position: THREE.Vector3, tangent: THREE.Vector3, isFinished: boolean } {
    this.elapsed += dt;
    const progress = Math.min( 1, this.elapsed / this.duration );
    
    // Accelerating motion curve (starts slow, gains speed)
    const eased = this.easing( progress );

    const position = this.curve.getPoint( eased );
    const tangent = this.curve.getTangent( eased );
    if ( tangent.lengthSq() > 0.0001 )
      tangent.normalize();
    else
      tangent.set( 0, 0, 1 ); // fallback

    return {
      position,
      tangent,
      isFinished: progress >= 1
    };
  }

  public isFinished(): boolean {
    return this.elapsed >= this.duration;
  }
}
